/**
 * EmptyStates - Beautiful, helpful empty states with illustrations
 * 
 * Provides friendly empty state UIs for various scenarios
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Store, Upload, Calendar, TrendingUp, FileQuestion } from 'lucide-react';

interface EmptyStateProps {
  type: 'no-retailers' | 'no-data' | 'no-week-data' | 'no-matches';
  onAction?: () => void;
  actionLabel?: string;
}

export function EmptyState({ type, onAction, actionLabel }: EmptyStateProps) {
  const config = getEmptyStateConfig(type);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="bg-white border-2 border-gray-200 rounded-2xl p-12 text-center"
    >
      {/* Illustrated Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
        className="mb-6 inline-block"
      >
        <div className={`w-24 h-24 rounded-full ${config.bgColor} flex items-center justify-center mx-auto relative`}>
          {/* Background circles for depth */}
          <div className={`absolute inset-0 rounded-full ${config.bgColor} opacity-50 scale-110`} />
          <div className={`absolute inset-0 rounded-full ${config.bgColor} opacity-30 scale-125`} />
          
          {/* Icon */}
          <config.Icon className={`w-12 h-12 ${config.iconColor} relative z-10`} />
        </div>
      </motion.div>

      {/* Title */}
      <h3 className="text-2xl font-bold text-gray-900 mb-3">
        {config.title}
      </h3>

      {/* Description */}
      <p className="text-gray-600 mb-6 max-w-md mx-auto leading-relaxed">
        {config.description}
      </p>

      {/* Action Button */}
      {(onAction || config.defaultAction) && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onAction || config.defaultAction}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${config.buttonClass}`}
        >
          {actionLabel || config.defaultActionLabel}
        </motion.button>
      )}

      {/* Secondary suggestion */}
      {config.secondaryText && (
        <p className="text-sm text-gray-500 mt-4">
          {config.secondaryText}
        </p>
      )}
    </motion.div>
  );
}

function getEmptyStateConfig(type: EmptyStateProps['type']) {
  switch (type) {
    case 'no-retailers':
      return {
        Icon: Store,
        bgColor: 'bg-blue-50',
        iconColor: 'text-blue-600',
        title: 'No Retailers Configured',
        description: 'Get started by adding your first retail partner. Connect your retail accounts to unlock powerful analytics and insights.',
        buttonClass: 'bg-indigo-600 text-white hover:bg-indigo-700',
        defaultActionLabel: 'Contact Support',
        defaultAction: () => window.location.href = 'mailto:support@patterniqai.com',
        secondaryText: 'Need help getting started? Our team is here to assist you.'
      };

    case 'no-data':
      return {
        Icon: Upload,
        bgColor: 'bg-purple-50',
        iconColor: 'text-purple-600',
        title: 'No Data Available',
        description: 'Upload your retail data to see comprehensive analytics, performance metrics, and actionable insights for this retailer.',
        buttonClass: 'bg-purple-600 text-white hover:bg-purple-700',
        defaultActionLabel: 'Upload Data',
        defaultAction: () => window.location.href = '/admin/data-upload',
        secondaryText: 'Accepted formats: Excel (.xlsx), CSV'
      };

    case 'no-week-data':
      return {
        Icon: Calendar,
        bgColor: 'bg-amber-50',
        iconColor: 'text-amber-600',
        title: 'No Data for This Week',
        description: 'The selected week doesn\'t have any data yet. Try selecting a different week or upload data for this time period.',
        buttonClass: 'bg-amber-600 text-white hover:bg-amber-700',
        defaultActionLabel: 'Go to Latest Week',
        secondaryText: 'Data is typically uploaded within 48 hours of week end'
      };

    case 'no-matches':
      return {
        Icon: FileQuestion,
        bgColor: 'bg-gray-50',
        iconColor: 'text-gray-600',
        title: 'No Retailers Match Your Filters',
        description: 'Try adjusting your filter settings to see more results. You can change the status filter or sort order above.',
        buttonClass: 'bg-gray-600 text-white hover:bg-gray-700',
        defaultActionLabel: 'Reset Filters',
        secondaryText: null
      };
  }
}

// Individual Empty State Components for convenience

export function NoRetailersEmpty({ onAction }: { onAction?: () => void }) {
  return <EmptyState type="no-retailers" onAction={onAction} />;
}

export function NoDataEmpty({ onAction }: { onAction?: () => void }) {
  return <EmptyState type="no-data" onAction={onAction} />;
}

export function NoWeekDataEmpty({ onAction }: { onAction?: () => void }) {
  return <EmptyState type="no-week-data" onAction={onAction} />;
}

export function NoMatchesEmpty({ onAction }: { onAction?: () => void }) {
  return <EmptyState type="no-matches" onAction={onAction} />;
}

